// components/doctors/Doctors.jsx
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Grid,
  Typography,
  Button,
  CircularProgress,
  Chip,
  Paper,
  Stack,
  Autocomplete,
  TextField,
  TablePagination,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Divider
} from '@mui/material';
import {
  Add as AddIcon,
  Person as PersonIcon,
  FilterList,
  Clear as ClearIcon,
  FileDownload as FileDownloadIcon,
  Description as DescriptionIcon,
  TableChart as TableChartIcon,
  LocalHospital as SpecializationIcon,
  SupportAgent as RepresentativeIcon,
  KeyboardArrowDown as ArrowDownIcon
} from '@mui/icons-material';
import { toast } from 'react-toastify';

import { doctorAPI, doctorSpecializationAPI } from '../../services/api';
import representativeService   from '../../services/representativeService';
import { searchRepresentatives, searchSpecializations } from '../../services/optimizedSearchService';
import { useAuth } from '../../context/AuthContext';
import { useRTL } from '../../hooks/useRTL';
import NoPrivilege from '../../components/common/NoPrivilege';
import NoContent from '../../components/common/NoContent';
import ConfirmDialog from '../../components/common/ConfirmDialog';
import SearchBar from '../../components/common/SearchBar';
import MultiSelectSearchAutocomplete from '../../components/common/MultiSelectSearchAutocomplete';
import DoctorCard from './doctorCard';
import AddDoctorDialog from './addDoctor.js';

// Export format options
const EXPORT_FORMATS = {
  simple: {
    key: 'simple',
    labelKey: 'doctors.export.simple',
    defaultLabel: 'Simple Export',
    descriptionKey: 'doctors.export.simpleDesc',
    defaultDescription: 'Basic doctor information',
    icon: <DescriptionIcon fontSize="small" />
  },
  detailed: {
    key: 'detailed',
    labelKey: 'doctors.export.detailed',
    defaultLabel: 'Detailed Export',
    descriptionKey: 'doctors.export.detailedDesc',
    defaultDescription: 'Includes qualifications and full details',
    icon: <TableChartIcon fontSize="small" />
  },
  bySpecialization: {
    key: 'bySpecialization',
    labelKey: 'doctors.export.bySpecialization',
    defaultLabel: 'Export by Specialization',
    descriptionKey: 'doctors.export.bySpecializationDesc',
    defaultDescription: 'Grouped by medical specialization',
    icon: <SpecializationIcon fontSize="small" />
  },
  byRepresentative: {
    key: 'byRepresentative',
    labelKey: 'doctors.export.byRepresentative',
    defaultLabel: 'Export by Representative',
    descriptionKey: 'doctors.export.byRepresentativeDesc',
    defaultDescription: 'Grouped by sales representative',
    icon: <RepresentativeIcon fontSize="small" />
  }
};

const Doctors = () => {
  const { t } = useTranslation();
  const { user, isSuperAdmin } = useAuth();
  const { isRTL } = useRTL();

  // Filter options
  const SORT_BY_OPTIONS = [
    { value: 'name', label: t('common.name', 'Name') },
    { value: 'specialization', label: t('doctors.specialization', 'Specialization') },
    { value: 'experience', label: t('doctors.experience', 'Experience') },
    { value: 'createdAt', label: t('common.createdAt', 'Created At') },
    { value: 'totalPatientsReferred', label: t('doctors.patientsReferred', 'Patients Referred') },
  ];

  const SORT_ORDER_OPTIONS = [
    { value: 'asc', label: t('common.ascending', 'Ascending') },
    { value: 'desc', label: t('common.descending', 'Descending') },
  ];

  const STATUS_OPTIONS = [
    { value: 'all', label: t('common.all', 'All') },
    { value: 'active', label: t('common.active', 'Active') },
    { value: 'inactive', label: t('common.inactive', 'Inactive') },
  ];

  // States
  const [doctors, setDoctors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openDialog, setOpenDialog] = useState(false);
  const [selectedDoctor, setSelectedDoctor] = useState(null);
  const [openConfirm, setOpenConfirm] = useState(false);
  const [doctorToDelete, setDoctorToDelete] = useState(null);
  const [privilegeError, setPrivilegeError] = useState(false);

  // Filter data (for initial options and allItems)
  const [representatives, setRepresentatives] = useState([]);
  const [specializations, setSpecializations] = useState([]);

  // Pagination state
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(12);
  const [total, setTotal] = useState(0);

  // Export states
  const [exporting, setExporting] = useState(false);
  const [exportMenuAnchor, setExportMenuAnchor] = useState(null);
  const exportMenuOpen = Boolean(exportMenuAnchor);

  // Consolidated filter state - using arrays for multi-select
  const [filters, setFilters] = useState({
    search: '',
    status: 'all',
    representativeIds: [],
    specializationIds: [],
    sortBy: 'name',
    sortOrder: 'asc',
  });

  // Permissions
  const canView = isSuperAdmin || user?.privileges?.some(p => p.module === 'doctors' && p.operations.includes('view'));
  const canAdd = isSuperAdmin || user?.privileges?.some(p => p.module === 'doctors' && p.operations.includes('create'));
  const canEdit = isSuperAdmin || user?.privileges?.some(p => p.module === 'doctors' && p.operations.includes('update'));
  const canDelete = isSuperAdmin || user?.privileges?.some(p => p.module === 'doctors' && p.operations.includes('delete'));
  const canExport = canView;

  // Check if any filter is active
  const hasActiveFilters = filters.search ||
    filters.status !== 'all' ||
    (filters.representativeIds && filters.representativeIds.length > 0) ||
    (filters.specializationIds && filters.specializationIds.length > 0);

  // ============== Memoized Initial Options ==============
  const representativeOptions = useMemo(() => representatives.slice(0, 20), [representatives]);
  const specializationOptions = useMemo(() => specializations.slice(0, 20), [specializations]);

  // ============== Label Functions ==============
  const getRepresentativeOptionLabel = useCallback(
    (option) => option?.name || option?.username || '',
    []
  );

  const getSpecializationOptionLabel = useCallback(
    (option) => option?.name || '',
    []
  );

  // ============== Change Handlers ==============
  const handleRepresentativeChange = useCallback((ids) => {
    setPage(0);
    setFilters(prev => ({ ...prev, representativeIds: ids }));
  }, []);

  const handleSpecializationChange = useCallback((ids) => {
    setPage(0);
    setFilters(prev => ({ ...prev, specializationIds: ids }));
  }, []);

  const handleFilterChange = useCallback((key, value) => {
    setPage(0);
    setFilters(prev => ({ ...prev, [key]: value }));
  }, []);

  const handleClearFilters = useCallback(() => {
    setPage(0);
    setFilters({
      search: '',
      status: 'all',
      representativeIds: [],
      specializationIds: [],
      sortBy: 'name',
      sortOrder: 'asc',
    });
  }, []);

  // Fetch filter options (representatives and specializations)
  useEffect(() => {
    const fetchFilterOptions = async () => {
      if (!canView) return;

      try {
        // Fetch representatives
        const repResponse = await representativeService.getAllRepresentatives({ limit: 50 });
        setRepresentatives(repResponse.data.data || repResponse.data || []);
      } catch (err) {
        console.error('Error fetching representatives:', err);
      }

      try {
        // Fetch specializations
        const specResponse = await doctorSpecializationAPI.getAll({ limit: 50 });
        setSpecializations(specResponse.data.data || specResponse.data || []);
      } catch (err) {
        console.error('Error fetching specializations:', err);
      }
    };

    fetchFilterOptions();
  }, [canView]);

  // Fetch doctors
  const fetchDoctors = useCallback(async () => {
    if (!canView) return;

    setLoading(true);
    setPrivilegeError(false);

    try {
      const params = {
        page: page + 1,
        limit: rowsPerPage,
        sortBy: filters.sortBy,
        sortOrder: filters.sortOrder,
      };

      if (filters.search) {
        params.search = filters.search;
      }

      if (filters.status !== 'all') {
        params.isActive = filters.status === 'active';
      }

      // Send array of representative IDs
      if (filters.representativeIds && filters.representativeIds.length > 0) {
        params.representativeIds = filters.representativeIds;
      }

      // Send array of specialization IDs
      if (filters.specializationIds && filters.specializationIds.length > 0) {
        params.specializationIds = filters.specializationIds;
      }

      const response = await doctorAPI.getAll(params);
      setDoctors(response.data.data || []);
      setTotal(response.data.pagination?.total || 0);
    } catch (err) {
      console.error('Fetch doctors error:', err);
      const backendMessage = err.response?.data?.message;
      if (err.response?.status === 403 || (backendMessage && backendMessage.toLowerCase().includes('privilege'))) {
        setPrivilegeError(true);
      } else {
        toast.error(backendMessage || t('doctors.fetchError', 'Failed to fetch doctors'));
      }
      setDoctors([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }, [page, rowsPerPage, filters, canView, t]);

  useEffect(() => {
    fetchDoctors();
  }, [fetchDoctors]);

  // Export menu handlers
  const handleExportMenuOpen = (event) => {
    setExportMenuAnchor(event.currentTarget);
  };

  const handleExportMenuClose = () => {
    setExportMenuAnchor(null);
  };

  // Export handler
  const handleExport = async (format = 'simple') => {
    handleExportMenuClose();
    setExporting(true);

    try {
      // Build export params from current filters
      const params = {
        sortBy: filters.sortBy,
        sortOrder: filters.sortOrder,
      };

      if (filters.search) params.search = filters.search;
      if (filters.representativeIds && filters.representativeIds.length > 0) {
        params.representativeIds = filters.representativeIds;
      }
      if (filters.specializationIds && filters.specializationIds.length > 0) {
        params.specializationIds = filters.specializationIds;
      }
      if (filters.status !== 'all') params.isActive = filters.status === 'active';

      let response;
      let filenamePrefix = 'doctors';

      // Call appropriate API based on format
      console.log("params:", params);
      switch (format) {
        case 'detailed':
          response = await doctorAPI.exportDetailed(params);
          filenamePrefix = 'doctors_detailed';
          break;
        case 'bySpecialization':
          response = await doctorAPI.exportBySpecialization(params);
          filenamePrefix = 'doctors_by_specialization';
          break;
        case 'byRepresentative':
          response = await doctorAPI.exportByRepresentative(params);
          filenamePrefix = 'doctors_by_representative';
          break;
        case 'simple':
        default:
          params.exportFormat = 'simple';
          response = await doctorAPI.export(params);
          filenamePrefix = 'doctors';
          break;
      }

      // Create blob and trigger download
      const blob = new Blob([response.data], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
      });

      // Create download link
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;

      // Generate filename with date
      const date = new Date().toISOString().split('T')[0];
      link.download = `${filenamePrefix}_${date}.xlsx`;

      // Trigger download
      document.body.appendChild(link);
      link.click();

      // Cleanup
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      toast.success(t('doctors.exportSuccess', 'Doctors exported successfully'));
    } catch (err) {
      console.error('Export error:', err);
      toast.error(
        err.response?.data?.message ||
        t('doctors.exportError', 'Failed to export doctors')
      );
    } finally {
      setExporting(false);
    }
  };

  // Dialog handlers
  const handleOpenDialog = (doctor = null) => {
    setSelectedDoctor(doctor);
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setSelectedDoctor(null);
  };

  const handleDoctorSuccess = () => {
    handleCloseDialog();
    fetchDoctors();
  };

  // Delete handlers
  const handleDeleteClick = (doctor) => {
    setDoctorToDelete(doctor);
    setOpenConfirm(true);
  };

  const handleDelete = async () => {
    if (!doctorToDelete) return;

    try {
      await doctorAPI.delete(doctorToDelete._id);
      toast.success(t('doctors.deleteSuccess', 'Doctor deleted successfully'));

      // If we deleted the last item on the current page, go to previous page
      if (doctors.length === 1 && page > 0) {
        setPage(page - 1);
      } else {
        fetchDoctors();
      }
    } catch (err) {
      console.error('Delete error:', err);
      toast.error(err.response?.data?.message || t('doctors.deleteError', 'Failed to delete doctor'));
    } finally {
      setOpenConfirm(false);
      setDoctorToDelete(null);
    }
  };

  // Pagination handlers
  const handlePageChange = (event, newPage) => {
    setPage(newPage);
  };

  const handleRowsPerPageChange = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // Helper function to get selected items names for chips
  const getSelectedRepresentativeNames = useCallback(() => {
    return filters.representativeIds
      .map(id => representatives.find(r => r._id === id))
      .filter(Boolean)
      .map(r => r.name || r.username);
  }, [filters.representativeIds, representatives]);

  const getSelectedSpecializationNames = useCallback(() => {
    return filters.specializationIds
      .map(id => specializations.find(s => s._id === id))
      .filter(Boolean)
      .map(s => s.name);
  }, [filters.specializationIds, specializations]);

  // Permission check
  if (!canView || privilegeError) {
    return <NoPrivilege message={t('doctors.noPrivilegeError', 'You do not have permission to view doctors.')} />;
  }

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box display="flex" alignItems="center" gap={2}>
          <Typography variant="h4" component="h1">
            {t('doctors.title', 'Doctors')}
          </Typography>
          {!loading && (
            <Chip
              label={`${total} ${t('common.total', 'total')}`}
              color="primary"
              variant="outlined"
              size="small"
            />
          )}
        </Box>
        <Stack direction="row" spacing={1}>
          {/* Export Button with Dropdown */}
          {canExport && (
            <>
              <Button
                variant="outlined"
                startIcon={exporting ? <CircularProgress size={18} /> : <FileDownloadIcon />}
                endIcon={<ArrowDownIcon />}
                onClick={handleExportMenuOpen}
                disabled={exporting || loading}
                aria-controls={exportMenuOpen ? 'doctor-export-menu' : undefined}
                aria-haspopup="true"
                aria-expanded={exportMenuOpen ? 'true' : undefined}
              >
                {exporting
                  ? t('common.exporting', 'Exporting...')
                  : t('common.export', 'Export')
                }
              </Button>
              <Menu
                id="doctor-export-menu"
                anchorEl={exportMenuAnchor}
                open={exportMenuOpen}
                onClose={handleExportMenuClose}
                MenuListProps={{
                  'aria-labelledby': 'doctor-export-button',
                }}
                anchorOrigin={{
                  vertical: 'bottom',
                  horizontal: 'right',
                }}
                transformOrigin={{
                  vertical: 'top',
                  horizontal: 'right',
                }}
              >
                {/* Simple Export */}
                <MenuItem onClick={() => handleExport('simple')}>
                  <ListItemIcon>
                    {EXPORT_FORMATS.simple.icon}
                  </ListItemIcon>
                  <ListItemText
                    primary={t(EXPORT_FORMATS.simple.labelKey, EXPORT_FORMATS.simple.defaultLabel)}
                    secondary={t(EXPORT_FORMATS.simple.descriptionKey, EXPORT_FORMATS.simple.defaultDescription)}
                  />
                </MenuItem>

                <Divider />

                {/* Detailed Export */}
                <MenuItem onClick={() => handleExport('detailed')}>
                  <ListItemIcon>
                    {EXPORT_FORMATS.detailed.icon}
                  </ListItemIcon>
                  <ListItemText
                    primary={t(EXPORT_FORMATS.detailed.labelKey, EXPORT_FORMATS.detailed.defaultLabel)}
                    secondary={t(EXPORT_FORMATS.detailed.descriptionKey, EXPORT_FORMATS.detailed.defaultDescription)}
                  />
                </MenuItem>

                <Divider />

                {/* Export by Specialization */}
                <MenuItem onClick={() => handleExport('bySpecialization')}>
                  <ListItemIcon>
                    {EXPORT_FORMATS.bySpecialization.icon}
                  </ListItemIcon>
                  <ListItemText
                    primary={t(EXPORT_FORMATS.bySpecialization.labelKey, EXPORT_FORMATS.bySpecialization.defaultLabel)}
                    secondary={t(EXPORT_FORMATS.bySpecialization.descriptionKey, EXPORT_FORMATS.bySpecialization.defaultDescription)}
                  />
                </MenuItem>

                <Divider />

                {/* Export by Representative */}
                <MenuItem onClick={() => handleExport('byRepresentative')}>
                  <ListItemIcon>
                    {EXPORT_FORMATS.byRepresentative.icon}
                  </ListItemIcon>
                  <ListItemText
                    primary={t(EXPORT_FORMATS.byRepresentative.labelKey, EXPORT_FORMATS.byRepresentative.defaultLabel)}
                    secondary={t(EXPORT_FORMATS.byRepresentative.descriptionKey, EXPORT_FORMATS.byRepresentative.defaultDescription)}
                  />
                </MenuItem>
              </Menu>
            </>
          )}
          {canAdd && (
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => handleOpenDialog()}
            >
              {t('doctors.addTitle', 'Add Doctor')}
            </Button>
          )}
        </Stack>
      </Box>

      {/* Filters Paper */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Stack direction="row" alignItems="center" gap={2} mb={2}>
          <FilterList />
          <Typography variant="h6">{t('filters', 'Filters')}</Typography>
          {hasActiveFilters && (
            <Chip
              label={t('common.filtersApplied', 'Filters will be applied to export')}
              size="small"
              color="info"
              variant="outlined"
            />
          )}
        </Stack>

        {/* Filter Row 1 - Search and Filters */}
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems="center" mb={2}>
          <SearchBar
            onSearch={(query) => handleFilterChange('search', query)}
            initialValue={filters.search}
            placeholder={t('doctors.searchPlaceholder', 'Search doctors...')}
            sx={{ flexGrow: 1, minWidth: '250px' }}
          />

          {/* Status Filter */}
          <Autocomplete
            options={STATUS_OPTIONS}
            getOptionLabel={(option) => option.label}
            value={STATUS_OPTIONS.find(opt => opt.value === filters.status)}
            onChange={(event, newValue) => handleFilterChange('status', newValue?.value || 'all')}
            renderInput={(params) => (
              <TextField {...params} label={t('common.status', 'Status')} size="small" />
            )}
            sx={{ minWidth: 150 }}
            disableClearable
          />

          {/*   Representative Filter - Multi-Select with Real-Time Search */}
          <MultiSelectSearchAutocomplete
            label={t('doctors.representative', 'Representative')}
            searchFunction={searchRepresentatives}
            value={filters.representativeIds || []}
            onChange={handleRepresentativeChange}
            initialOptions={representativeOptions}
            allItems={representatives}
            getOptionLabel={getRepresentativeOptionLabel}
            minWidth={220}
          />

          {/*   Specialization Filter - Multi-Select with Real-Time Search */}
          <MultiSelectSearchAutocomplete
            label={t('doctors.specialization', 'Specialization')}
            searchFunction={searchSpecializations}
            value={filters.specializationIds || []}
            onChange={handleSpecializationChange}
            initialOptions={specializationOptions}
            allItems={specializations}
            getOptionLabel={getSpecializationOptionLabel}
            minWidth={220}
          />
          {/* Sort By */}
          <Autocomplete
            options={SORT_BY_OPTIONS}
            getOptionLabel={(option) => option.label}
            value={SORT_BY_OPTIONS.find(opt => opt.value === filters.sortBy)}
            onChange={(event, newValue) => handleFilterChange('sortBy', newValue?.value || 'name')}
            renderInput={(params) => (
              <TextField {...params} label={t('common.sortBy', 'Sort By')} size="small" />
            )}
            sx={{ minWidth: 180 }}
            disableClearable
          />

          {/* Sort Order */}
          <Autocomplete
            options={SORT_ORDER_OPTIONS}
            getOptionLabel={(option) => option.label}
            value={SORT_ORDER_OPTIONS.find(opt => opt.value === filters.sortOrder)}
            onChange={(event, newValue) => handleFilterChange('sortOrder', newValue?.value || 'asc')}
            renderInput={(params) => (
              <TextField {...params} label={t('common.order', 'Order')} size="small" />
            )}
            sx={{ minWidth: 150 }}
            disableClearable
          />

          {/* Clear Filters */}
          {hasActiveFilters && (
            <Button
              variant="text"
              color="inherit"
              onClick={handleClearFilters}
              startIcon={<ClearIcon />}
              size="small"
            >
              {t('common.clearFilters', 'Clear')}
            </Button>
          )}
        </Stack>
      </Paper>

      {/* Active Filters Display */}
      {hasActiveFilters && (
        <Box display="flex" gap={1} flexWrap="wrap" mb={2}>
          {filters.search && (
            <Chip
              label={`${t('common.search', 'Search')}: ${filters.search}`}
              size="small"
              onDelete={() => handleFilterChange('search', '')}
            />
          )}
          {filters.status !== 'all' && (
            <Chip
              label={`${t('common.status', 'Status')}: ${STATUS_OPTIONS.find(o => o.value === filters.status)?.label}`}
              size="small"
              color={filters.status === 'active' ? 'success' : 'default'}
              onDelete={() => handleFilterChange('status', 'all')}
            />
          )}
          {/* Representative Chips */}
          {filters.representativeIds && filters.representativeIds.length > 0 && (
            getSelectedRepresentativeNames().map((name, index) => (
              <Chip
                key={`rep-${filters.representativeIds[index]}`}
                label={`${t('doctors.representative', 'Representative')}: ${name}`}
                size="small"
                color="secondary"
                onDelete={() => {
                  const newIds = filters.representativeIds.filter((_, i) => i !== index);
                  handleRepresentativeChange(newIds);
                }}
              />
            ))
          )}
          {/* Specialization Chips */}
          {filters.specializationIds && filters.specializationIds.length > 0 && (
            getSelectedSpecializationNames().map((name, index) => (
              <Chip
                key={`spec-${filters.specializationIds[index]}`}
                label={`${t('doctors.specialization', 'Specialization')}: ${name}`}
                size="small"
                color="primary"
                onDelete={() => {
                  const newIds = filters.specializationIds.filter((_, i) => i !== index);
                  handleSpecializationChange(newIds);
                }}
              />
            ))
          )}
        </Box>
      )}

      {/* Results Info */}
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        {t('common.showing', 'Showing')} {total === 0 ? 0 : Math.min(page * rowsPerPage + 1, total)}-{Math.min((page + 1) * rowsPerPage, total)} {t('common.of', 'of')} {total} {t('common.items', 'items')}
      </Typography>

      {/* Content */}
      {loading ? (
        <Paper sx={{ p: 4, display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 300 }}>
          <CircularProgress />
        </Paper>
      ) : doctors.length === 0 ? (
        <Paper sx={{ p: 4 }}>
          <NoContent
            icon={<PersonIcon sx={{ fontSize: 64 }} />}
            title={
              hasActiveFilters
                ? t('doctors.noFilterResults', 'No doctors match your filters')
                : t('doctors.noData', 'No Doctors Found')
            }
            description={
              hasActiveFilters
                ? t('doctors.tryAdjustingFilters', 'Try adjusting your filter criteria')
                : t('doctors.noDataDescription', 'Start by adding your first doctor.')
            }
            action={
              canAdd && !hasActiveFilters && (
                <Button
                  variant="contained"
                  startIcon={<AddIcon />}
                  onClick={() => handleOpenDialog()}
                >
                  {t('doctors.addTitle', 'Add Doctor')}
                </Button>
              )
            }
          />
        </Paper>
      ) : (
        <>
          {/* Doctor Cards Grid */}
          <Grid
            container
            spacing={3}
            dir={isRTL ? 'rtl' : 'ltr'}
            sx={{
              flexDirection: isRTL ? 'row-reverse' : 'row',
              justifyContent: isRTL ? 'flex-end' : 'flex-start',
            }}
          >
            {doctors.map((doctor) => (
              <Grid item xs={12} sm={6} md={4} lg={3} key={doctor._id}>
                <DoctorCard
                  doctor={doctor}
                  canEdit={canEdit}
                  canDelete={canDelete}
                  onEdit={handleOpenDialog}
                  onDelete={handleDeleteClick}
                />
              </Grid>
            ))}
          </Grid>

          {/* Pagination */}
          <Paper sx={{ mt: 3 }}>
            <TablePagination
              component="div"
              count={total}
              page={page}
              onPageChange={handlePageChange}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={handleRowsPerPageChange}
              rowsPerPageOptions={[6, 12, 24, 48]}
              labelRowsPerPage={t('common.rowsPerPage', 'Items per page')}
            />
          </Paper>
        </>
      )}

      {/* Dialogs */}
      <AddDoctorDialog
        open={openDialog}
        onClose={handleCloseDialog}
        doctor={selectedDoctor}
        onSuccess={handleDoctorSuccess}
      />

      <ConfirmDialog
        open={openConfirm}
        title={t('doctors.deleteConfirmTitle', 'Delete Doctor')}
        message={
          doctorToDelete
            ? `${t('doctors.deleteConfirmMessage', 'Are you sure you want to delete')} "${doctorToDelete.name}"?`
            : t('doctors.deleteConfirmMessage', 'Are you sure you want to delete this doctor?')
        }
        onConfirm={handleDelete}
        onClose={() => {
          setOpenConfirm(false);
          setDoctorToDelete(null);
        }}
      />
    </Box>
  );
};

export default Doctors;